<template>
  <div v-if="isLoading">
    <v-skeleton-loader type="card" />
  </div>
  <div v-else style="background-color: #f9f9f9">
    <v-expansion-panels v-model="activeDispatchPanel" mandatory>
      <transition-group name="list-complete" tag="div" class="full-width">
        <MessageDispatchPanel
          v-for="(settings, index) in schedule_settings"
          :key="settings.id"
          :settings="settings"
          :is-addition-disabled="additionValidations"
          :active-panel="activeDispatchPanel"
          :panel-index="index"
          :has-error="settingsErrors[settings.id].hasError"
          :error-messages="settingsErrors[settings.id].messages"
          @duplicate="duplicate(settings.id)"
          @delete="remove(settings.id)"
          @update-settings="updateSettings(settings.id, $event)"
          @update-active-panel="activeDispatchPanel = $event"
        />
      </transition-group>
    </v-expansion-panels>
    <div>
      <v-btn class="add-dispatch-btn" block large outlined :disabled="additionValidations || isMariaCampaignFinished" @click="addNewDispatch">
        <v-icon left>mdi-plus</v-icon>
        Adicionar novo disparo
      </v-btn>
    </div>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import { v4 as uuidv4 } from 'uuid'
import MessageDispatchPanel from './MessageDispatchPanel.vue'
import channelsDefaultMessages from '@/modules/maria/helpers/maria-default-values'
import { mariaCampaign } from '@/modules/maria/campanha-da-maria-v2/mixins/maria-campaign'
import { bestOfferValidations } from '@/modules/maria/campanha-da-maria-v2/mixins/best-offer-validations'

export default {
  components: { MessageDispatchPanel },
  mixins: [mariaCampaign, bestOfferValidations],
  data() {
    return {
      isLoading: true,
      weekDays: ['segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado', 'domingo'],
      invalidSettings: [],
      errorMessages: {
        duplicateWeekDay: 'No modelo recorrente por dia da semana, não é possível configurar mais de um disparo para o mesmo dia.',
        duplicateSendDate: 'Não é possível configurar disparos para o mesmo dia no modelo de data fixa.',
        invalidRangeDate: 'Não é possível configurar disparos para os dias fora do período de validade.',
        invalidChannel: 'Não é possível configurar disparos sem ter pelo menos um canal ativo.',
        generalError: 'Existem configurações inválidas nos disparos. Verifique os painéis destacados.',
        emptyWeekDay: 'Dia da semana não pode estar vazio.',
        emptySendDate: 'Data de envio não pode estar vazia.',
        invalidPrices:
          'Para utilizar as subvariáveis de preço você precisa configurar seus respectivos valores no passo 2. Por favor volte e preencha os campos deixados em branco ou remova os preços das suas mensagens.',
        notEnoughProducts:
          'Para utilizar essa quantidade de produtos na variável de melhor oferta, você precisa ter pelo menos essa quantidade de produtos no passo 2. Por favor volte e insira os produtos faltantes.',
      },
    }
  },
  computed: {
    schedule_settings: {
      get() {
        return this.campaign.messages_dispatcher.settings.schedule_settings
      },
      set(value) {
        this.setOmniCampaignKey({ key: 'messages_dispatcher.settings.schedule_settings', payload: value })
      },
    },
    isRecurrentModel() {
      return this.campaign?.messages_dispatcher?.settings?.model === 'RECURRENT'
    },
    isStaticModel() {
      return this.campaign?.messages_dispatcher?.settings?.model === 'STATIC_DATE'
    },
    additionValidations() {
      if (this.isRecurrentModel && this.schedule_settings.length >= 7) return true
      return false
    },
    // TODO: Jogar no mixin
    findEarliestStartDate() {
      let startDate = DateTime.min(...this.campaign.offer_schedules.map((offer) => DateTime.fromISO(offer.start_date))) || DateTime.now()
      if (this.campaignHasOffers) {
        return startDate < DateTime.now() ? DateTime.now() : startDate
      }
      return startDate
    },
    // TODO: Jogar no mixin
    findLatestEndDate() {
      return DateTime.max(...this.campaign.offer_schedules.map((offer) => DateTime.fromISO(offer.end_date)))
    },
    activeDispatchPanel: {
      get() {
        return this.$store.getters.activeDispatchPanel
      },
      set(value) {
        this.$store.commit('SET_ACTIVE_DISPATCH_PANEL', value)
      },
    },
    settingsErrors() {
      return this.schedule_settings.reduce((acc, setting) => {
        acc[setting.id] = {
          hasError: !!this.invalidSettings.find((s) => s.id === setting.id),
          messages: this.invalidSettings.find((s) => s.id === setting.id)?.messages || [],
        }
        return acc
      }, {})
    },
  },
  watch: {
    'campaign.messages_dispatcher.settings.model'() {
      if (this.isRecurrentModel) {
        this.setDefaultWeekDay()
      } else if (this.isStaticModel) {
        this.setDefaultSendDate()
      }
      this.sortDays()
      this.clearErrors()
    },
  },
  mounted() {
    if (this.itsGetCampaignFromApi && this.schedule_settings.length) {
      this.formatScheduleSettings()
    }
    if (!this.schedule_settings.length) this.addNewDispatch()
    this.isLoading = false
  },
  methods: {
    focus(settingsId) {
      const targetIndex = this.schedule_settings.findIndex((setting) => setting.id === settingsId)
      if (targetIndex !== -1) {
        this.$nextTick(() => {
          const targetPanel = this.$el.querySelectorAll('.message-dispatch-panel')[targetIndex]
          if (targetPanel) {
            targetPanel.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            })
            targetPanel.focus()
          }
        })
      }
    },
    clearErrors() {
      this.invalidSettings = []
    },
    scheduleSettingsById(id) {
      return this.schedule_settings.find((setting) => setting.id === id)
    },
    formatMessage(channel, message) {
      if (!message || !message.text) return channelsDefaultMessages[channel]

      const regexBestOfferWithoutAmount = /\[melhor oferta\]/i
      const hasBestOfferWithoutAmount = regexBestOfferWithoutAmount.test(message.text)
      if (hasBestOfferWithoutAmount && message.amount_best_offers) {
        message.text = message.text.replace(/\[melhor oferta\]/i, `[${message.amount_best_offers} melhor oferta]`)
      }

      return message
    },
    formatPushMessage(channel, message) {
      if (!message || (!message.text && !message.title)) return channelsDefaultMessages[channel]

      const regexBestOfferWithoutAmount = /\[melhor oferta\]/i
      const hasBestOfferWithoutAmount = regexBestOfferWithoutAmount.test(message.text)
      const hasBestOfferWithoutAmountTitle = regexBestOfferWithoutAmount.test(message.title)
      const hasBestOfferWithoutAmountInnerText = regexBestOfferWithoutAmount.test(message.inner_message_text)
      const hasBestOfferWithoutAmountInnerTitle = regexBestOfferWithoutAmount.test(message.inner_message_title)
      if (hasBestOfferWithoutAmount && message.amount_best_offers) {
        message.text = message.text.replace(/\[melhor oferta\]/i, `[${message.amount_best_offers} melhor oferta]`)
      }
      if (hasBestOfferWithoutAmountTitle && message.amount_best_offers_title) {
        message.title = message.title.replace(/\[melhor oferta\]/i, `[${message.amount_best_offers_title} melhor oferta]`)
      }

      if (hasBestOfferWithoutAmountInnerText && message.inner_amount_best_offers) {
        message.inner_message_text = message.inner_message_text.replace(/\[melhor oferta\]/i, `[${message.inner_amount_best_offers} melhor oferta]`)
      }
      if (hasBestOfferWithoutAmountInnerTitle && message.inner_amount_best_offers_title) {
        message.inner_message_title = message.inner_message_title.replace(/\[melhor oferta\]/i, `[${message.inner_amount_best_offers} melhor oferta]`)
      }

      return message
    },
    formatEmailMessage(channel, message) {
      if (message?.email_configuration_type === 'BUILDER') {
        if (Object.keys(message?.builder_email_configuration || {}).length > 0) {
          return message
        }
        return channelsDefaultMessages[channel]
      }

      if (message?.email_configuration_type === 'HTML') {
        if (message?.html_email_configuration?.text) {
          const { html_email_configuration: config } = message
          const regexBestOffer = /\[melhor oferta\]/i

          const replaceBestOffer = (text, amount) => {
            return text.replace(regexBestOffer, `[${amount} melhor oferta]`)
          }

          if (regexBestOffer.test(config.text) && config.amount_best_offers) {
            config.text = replaceBestOffer(config.text, config.amount_best_offers)
          }

          if (regexBestOffer.test(config.subject) && config.amount_best_offers_subject) {
            config.subject = replaceBestOffer(config.subject, config.amount_best_offers_subject)
          }

          return message
        }
        return channelsDefaultMessages[channel]
      }

      if (message?.email_configuration_type === 'SIMPLE') {
        if (!message?.simple_email_configuration?.text) {
          return channelsDefaultMessages[channel]
        }

        const { simple_email_configuration: config } = message
        const regexBestOffer = /\[melhor oferta\]/i

        const replaceBestOffer = (text, amount) => {
          return text.replace(regexBestOffer, `[${amount} melhor oferta]`)
        }

        if (regexBestOffer.test(config.text) && config.amount_best_offers) {
          config.text = replaceBestOffer(config.text, config.amount_best_offers)
        }

        if (regexBestOffer.test(config.subject) && config.amount_best_offers_subject) {
          config.subject = replaceBestOffer(config.subject, config.amount_best_offers_subject)
        }

        return message
      }

      return channelsDefaultMessages[channel]
    },
    setDefaultWeekDay() {
      this.schedule_settings.forEach((setting) => {
        if (!setting.week_day) setting.week_day = this.getNextWeekDay()
      })
    },
    setDefaultSendDate() {
      this.schedule_settings.forEach((setting) => {
        if (!setting.send_date) setting.send_date = this.getNextSendDate()
      })
    },
    formatWeekDay(weekDay) {
      if (!weekDay) return null
      const weekDaysReversed = {
        SUNDAY: 'domingo',
        MONDAY: 'segunda-feira',
        TUESDAY: 'terça-feira',
        WEDNESDAY: 'quarta-feira',
        THURSDAY: 'quinta-feira',
        FRIDAY: 'sexta-feira',
        SATURDAY: 'sábado',
      }
      return weekDaysReversed[weekDay] ?? weekDay
    },
    formatSendDate(sendDate) {
      if (!sendDate) return null

      const parsedDate = DateTime.fromISO(sendDate, { zone: 'utc' })
      if (!parsedDate.isValid) return sendDate
      return parsedDate.toISODate().split('-').reverse().join('/')
    },
    formatScheduleSettings() {
      this.isLoading = true
      this.schedule_settings = this.schedule_settings.map((setting, index) => {
        return {
          ...setting,
          index: index + 1,
          week_day: this.formatWeekDay(setting.week_day),
          send_date: this.formatSendDate(setting.send_date),
          channels: {
            ...(this.getChannelsPermissions['sms']
              ? {
                  sms: {
                    ...setting?.channels?.['sms'],
                    name: 'SMS',
                    icon: 'mdi-message-reply-text',
                    priority: setting?.channels['sms']?.priority,
                    active: setting?.channels['sms']?.active ?? true,
                    message: this.formatMessage('sms', setting?.channels['sms']?.message),
                  },
                }
              : {}),
            ...(this.getChannelsPermissions['whatsapp']
              ? {
                  whatsapp: {
                    ...setting?.channels?.['whatsapp'],
                    name: 'WhatsApp',
                    icon: 'mdi-whatsapp',
                    priority: setting?.channels['whatsapp']?.priority,
                    active: setting?.channels['whatsapp']?.active ?? true,
                    message: this.formatMessage('whatsapp', setting?.channels['whatsapp']?.message),
                  },
                }
              : {}),
            ...(this.getChannelsPermissions['push']
              ? {
                  push: {
                    ...setting?.channels?.['push'],
                    name: 'Push App',
                    icon: 'mdi-cellphone',
                    priority: setting?.channels['push']?.priority,
                    active: setting?.channels['push']?.active ?? true,
                    message: this.formatPushMessage('push', setting?.channels['push']?.message),
                  },
                }
              : {}),
            ...(this.getChannelsPermissions['email']
              ? {
                  email: {
                    ...setting?.channels?.['email'],
                    name: 'E-mail',
                    icon: 'mdi-email',
                    priority: setting?.channels['email']?.priority,
                    active: setting?.channels['email']?.active ?? true,
                    message: this.formatEmailMessage('email', setting?.channels['email']?.message),
                  },
                }
              : {}),
          },
        }
      })
      this.isLoading = false
    },
    hasDuplicateWeekDay() {
      const dayCount = new Map()
      const invalidIds = []

      for (const setting of this.schedule_settings) {
        if (dayCount.has(setting.week_day)) {
          invalidIds.push(setting.id)
          invalidIds.push(dayCount.get(setting.week_day))
        }
        dayCount.set(setting.week_day, setting.id)
      }

      if (this.schedule_settings.length > 7) {
        const excessSettings = this.schedule_settings.slice(7)
        excessSettings.forEach((setting) => invalidIds.push(setting.id))
      }

      return [...new Set(invalidIds)]
    },
    hasDuplicateSendDate() {
      const dayCount = new Map()
      const invalidIds = []
      for (const setting of this.schedule_settings) {
        if (dayCount.has(setting.send_date)) {
          invalidIds.push(setting.id)
        }
        dayCount.set(setting.send_date, setting.id)
      }
      return invalidIds
    },
    invalidRangeDate() {
      const invalidIds = []
      this.schedule_settings.forEach((dispatch) => {
        if (!dispatch.send_date) {
          invalidIds.push(dispatch.id)
          return
        }

        const dispatchDate = DateTime.fromFormat(dispatch.send_date, 'dd/MM/yyyy')
        if (!dispatchDate.isValid) {
          invalidIds.push(dispatch.id)
          return
        }

        const campaignStart = this.campaign.initial_date
          ? DateTime.fromFormat(this.campaign.initial_date, 'yyyy-MM-dd').startOf('day')
          : this.findEarliestStartDate.startOf('day')
        const campaignEnd = this.campaign.final_date
          ? DateTime.fromFormat(this.campaign.final_date, 'yyyy-MM-dd').endOf('day')
          : this.findLatestEndDate.endOf('day')
        if (dispatchDate < campaignStart || dispatchDate > campaignEnd) {
          invalidIds.push(dispatch.id)
        }
      })
      return invalidIds
    },
    hasValidChannel() {
      const invalidIds = []
      this.schedule_settings.forEach((settings) => {
        const hasValidChannel = Object.keys(settings.channels).some((channel) => {
          if (!settings.channels[channel]?.active) return false

          if (channel === 'email') {
            if (settings.channels[channel]?.message?.email_configuration_type === 'BUILDER') {
              return !!Object.keys(settings.channels[channel]?.message?.builder_email_configuration).length
            }
            if (settings.channels[channel]?.message?.email_configuration_type === 'SIMPLE') {
              return !!settings.channels[channel]?.message?.simple_email_configuration?.text
            }
            if (settings.channels[channel]?.message?.email_configuration_type === 'HTML') {
              return !!settings.channels[channel]?.message?.html_email_configuration?.text
            }
          }
          if (channel === 'push') {
            return !!(settings.channels[channel]?.message?.text || settings.channels[channel]?.message?.title)
          }
          return !!settings.channels[channel]?.message?.text
        })
        if (!hasValidChannel) {
          invalidIds.push(settings.id)
        }
      })
      return invalidIds
    },
    validate() {
      let isValid = true
      this.clearErrors()

      if (this.isRecurrentModel) {
        const invalidIds = this.hasDuplicateWeekDay()
        if (invalidIds.length > 0) {
          invalidIds.forEach((id) => {
            const existingSetting = this.invalidSettings.find((s) => s.id === id)
            if (existingSetting) {
              existingSetting.messages.push(this.errorMessages.duplicateWeekDay)
            } else {
              this.invalidSettings.push({
                id,
                messages: [this.errorMessages.duplicateWeekDay],
              })
            }
          })
          isValid = false
        }

        this.schedule_settings.forEach((setting) => {
          if (!setting.week_day) {
            const existingSetting = this.invalidSettings.find((s) => s.id === setting.id)
            if (existingSetting) {
              existingSetting.messages.push(this.errorMessages.emptyWeekDay)
            } else {
              this.invalidSettings.push({
                id: setting.id,
                messages: [this.errorMessages.emptyWeekDay],
              })
            }
            isValid = false
          }
        })
      }

      if (this.isStaticModel) {
        const duplicateIds = this.hasDuplicateSendDate()
        if (duplicateIds.length > 0) {
          duplicateIds.forEach((id) => {
            const existingSetting = this.invalidSettings.find((s) => s.id === id)
            if (existingSetting) {
              existingSetting.messages.push(this.errorMessages.duplicateSendDate)
            } else {
              this.invalidSettings.push({
                id,
                messages: [this.errorMessages.duplicateSendDate],
              })
            }
          })
          isValid = false
        }

        const rangeInvalidIds = this.invalidRangeDate()
        if (rangeInvalidIds.length > 0) {
          rangeInvalidIds.forEach((id) => {
            const existingSetting = this.invalidSettings.find((s) => s.id === id)
            if (existingSetting) {
              existingSetting.messages.push(this.errorMessages.invalidRangeDate)
            } else {
              this.invalidSettings.push({
                id,
                messages: [this.errorMessages.invalidRangeDate],
              })
            }
          })
          isValid = false
        }

        this.schedule_settings.forEach((setting) => {
          if (!setting.send_date) {
            const existingSetting = this.invalidSettings.find((s) => s.id === setting.id)
            if (existingSetting) {
              existingSetting.messages.push(this.errorMessages.emptySendDate)
            } else {
              this.invalidSettings.push({
                id: setting.id,
                messages: [this.errorMessages.emptySendDate],
              })
            }
            isValid = false
          }
        })
      }

      const channelInvalidIds = this.hasValidChannel()
      if (channelInvalidIds.length > 0) {
        channelInvalidIds.forEach((id) => {
          const existingSetting = this.invalidSettings.find((s) => s.id === id)
          if (existingSetting) {
            existingSetting.messages.push(this.errorMessages.invalidChannel)
          } else {
            this.invalidSettings.push({
              id,
              messages: [this.errorMessages.invalidChannel],
            })
          }
        })
        isValid = false
      }

      let messageValidations = []
      if (this.campaign.messages_dispatches_channels?.length > 0 && this.schedule_settings.length > 0) {
        for (const schedule of this.schedule_settings) {
          for (const channelKey of Object.keys(schedule.channels)) {
            const channel = schedule.channels[channelKey]
            if (channelKey === 'push') {
              const { inner_message_text, inner_message_title, text, title } = channel.message
              messageValidations.push(this.validateBestOfferMessage(inner_message_text, schedule, channelKey, channel.name))
              messageValidations.push(this.validateBestOfferMessage(inner_message_title, schedule, channelKey, channel.name))
              messageValidations.push(this.validateBestOfferMessage(text, schedule, channelKey, channel.name))
              messageValidations.push(this.validateBestOfferMessage(title, schedule, channelKey, channel.name))
            } else if (channelKey === 'email' && channel?.message?.email_configuration_type === 'SIMPLE') {
              const { text, subject } = channel.message.simple_email_configuration
              messageValidations.push(this.validateBestOfferMessage(text, schedule, channelKey, channel.name))
              messageValidations.push(this.validateBestOfferMessage(subject, schedule, channelKey, channel.name))
            } else {
              const { text } = channel.message
              messageValidations.push(this.validateBestOfferMessage(text, schedule, channelKey, channel.name))
            }
          }
        }
        isValid = messageValidations.every((isValid) => isValid)
      }

      if (!isValid) {
        const firstInvalidSetting = this.invalidSettings[0]
        if (firstInvalidSetting) {
          const index = this.schedule_settings.findIndex((setting) => setting.id === firstInvalidSetting.id)
          if (index !== -1) {
            this.$nextTick(() => {
              this.activeDispatchPanel = index
            })
          }
        }

        this.$snackbar({
          message: this.errorMessages.generalError,
          snackbarColor: 'error',
        })
      }

      return isValid
    },
    duplicate(settingsId) {
      if (this.additionValidations) return
      const foundSettings = this.scheduleSettingsById(settingsId)
      if (!foundSettings) return
      const newSettings = JSON.parse(
        JSON.stringify({
          ...foundSettings,
          id: uuidv4(),
        })
      )
      this.schedule_settings.push(newSettings)
      this.sortDays()
      const newItemIndex = this.schedule_settings.findIndex((setting) => setting.id === newSettings.id)

      this.$nextTick(() => {
        this.activeDispatchPanel = newItemIndex !== -1 ? newItemIndex : 0
      })
    },
    async remove(settingsId) {
      if (this.schedule_settings.length <= 1) {
        this.$snackbar({ message: 'É obrigatório ter pelo menos um disparo configurado.', snackbarColor: 'error' })
        return
      }
      const confirmed = await this.$confirm({
        message: 'Tem certeza que deseja excluir as configurações do disparo?',
        confirmText: 'Excluir',
        cancelText: 'Cancelar',
        confirmColor: 'vermelho-d20',
      })
        .then(() => true)
        .catch(() => false)

      if (confirmed) {
        const foundSettings = this.scheduleSettingsById(settingsId)
        if (!foundSettings) return
        this.schedule_settings = this.schedule_settings.filter((setting) => setting.id !== foundSettings.id)
      }
    },
    getNextWeekDay() {
      const usedDays = new Set(this.schedule_settings.map(({ week_day }) => week_day))
      const nextDay = this.weekDays.find((day) => !usedDays.has(day))
      return nextDay || ''
    },
    getNextSendDate() {
      if (!this.schedule_settings.length) return ''

      const today = DateTime.now().startOf('day')
      const campaignStart = this.campaign.initial_date
        ? DateTime.fromFormat(this.campaign.initial_date, 'yyyy-MM-dd').startOf('day')
        : this.findEarliestStartDate.startOf('day')
      const campaignEnd = this.findLatestEndDate
      const maxEndDate = campaignEnd?.isValid ? campaignEnd.startOf('day') : campaignStart.plus({ days: 30 }).startOf('day')

      const usedDates = new Set(
        this.schedule_settings.filter((item) => item.send_date).map((item) => DateTime.fromFormat(item.send_date, 'dd/MM/yyyy').toISODate())
      )

      let nextDate = DateTime.max(today.plus({ days: 1 }), campaignStart)

      while (nextDate <= maxEndDate) {
        if (!usedDates.has(nextDate.toISODate())) {
          return nextDate.toFormat('dd/MM/yyyy')
        }
        nextDate = nextDate.plus({ days: 1 })
      }

      return ''
    },
    sortDays() {
      if (this.isRecurrentModel) this.sortDataByWeekDays()
      if (this.isStaticModel) this.sortDataSendDates()
    },
    sortDataByWeekDays() {
      return this.schedule_settings.sort((a, b) => this.weekDays.indexOf(a.week_day) - this.weekDays.indexOf(b.week_day))
    },
    sortDataSendDates() {
      return this.schedule_settings.sort((a, b) => DateTime.fromFormat(a.send_date, 'dd/MM/yyyy') - DateTime.fromFormat(b.send_date, 'dd/MM/yyyy'))
    },
    addNewDispatch() {
      const newSettings = {
        id: uuidv4(),
        index: this.schedule_settings.length + 1,
        active: true,
        initial_hour: '06:00',
        final_hour: '23:00',
        week_day: this.getNextWeekDay(),
        send_date: this.getNextSendDate(),
        selector_channels_type: 'MARIA',
        channels: {
          ...(this.getChannelsPermissions['sms']
            ? {
                sms: {
                  name: 'SMS',
                  icon: 'mdi-message-reply-text',
                  priority: '',
                  active: true,
                  message: channelsDefaultMessages.sms,
                },
              }
            : {}),
          ...(this.getChannelsPermissions['whatsapp']
            ? {
                whatsapp: {
                  name: 'WhatsApp',
                  icon: 'mdi-whatsapp',
                  priority: '',
                  active: true,
                  senders: [],
                  targeting_mode: '',
                  message: channelsDefaultMessages.whatsapp,
                },
              }
            : {}),
          ...(this.getChannelsPermissions['push']
            ? {
                push: {
                  name: 'Push App',
                  icon: 'mdi-cellphone',
                  priority: '',
                  active: true,
                  message: channelsDefaultMessages.push,
                },
              }
            : {}),
          ...(this.getChannelsPermissions['email']
            ? {
                email: {
                  name: 'E-mail',
                  icon: 'mdi-email',
                  priority: '',
                  active: true,
                  message: channelsDefaultMessages.email,
                },
              }
            : {}),
        },
      }
      this.schedule_settings.push(newSettings)
      this.sortDays()
      const newItemIndex = this.schedule_settings.findIndex((setting) => setting.id === newSettings.id)

      this.$nextTick(() => {
        this.activeDispatchPanel = newItemIndex !== -1 ? newItemIndex : 0
      })
    },
    updateSettings(settingsId, newSettings) {
      this.clearErrors()
      const index = this.schedule_settings.findIndex((setting) => setting.id === settingsId)
      if (index !== -1) {
        this.schedule_settings.splice(index, 1, newSettings)
      }
      this.sortDays()
    },
    validateBestOfferMessage(message, schedule, channel, channelName) {
      let isValid = true

      if (!this.hasEnoughProducts(message)) {
        const existingSetting = this.invalidSettings.find((s) => s.id === schedule.id)
        if (existingSetting) {
          existingSetting.messages.push(`Canal ${channelName}: ${[this.errorMessages.notEnoughProducts]}`)
        } else {
          this.invalidSettings.push({
            id: schedule.id,
            messages: [`Canal ${channelName}: ${[this.errorMessages.notEnoughProducts]}`],
          })
        }
        isValid = false
      }

      if (!this.isPricesCorrect(channel, 'text', 'offer_sales', message)) {
        const existingSetting = this.invalidSettings.find((s) => s.id === schedule.id)
        if (existingSetting) {
          existingSetting.messages.push(`Canal ${channelName}: ${[this.errorMessages.invalidPrices]}`)
        } else {
          this.invalidSettings.push({
            id: schedule.id,
            messages: [`Canal ${channelName}: ${[this.errorMessages.invalidPrices]}`],
          })
        }
        isValid = false
      }

      return isValid
    },
  },
}
</script>

<style lang="scss" scoped>
.full-width {
  width: 100%;
}

.add-dispatch-btn {
  border-color: $marca-0-l40;
  color: $marca-0;
  text-transform: none;
  font-size: 16px;
  background: linear-gradient(0deg, rgba(210, 233, 255, 0.6), rgba(210, 233, 255, 0.6));
  border-radius: 8px;
  border-style: dashed;
  letter-spacing: 0.4000000059604645px;
}

.v-expansion-panels {
  width: 100%;

  :deep(.v-expansion-panel) {
    width: 100%;
  }
}

.list-complete-item {
  transition: all 0.5s ease;
  width: 100%;
}

.list-complete-enter-active,
.list-complete-leave-active {
  transition: all 0.5s ease;
}

.list-complete-enter-from,
.list-complete-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

.list-complete-move {
  transition: transform 0.5s ease;
}
</style>
